import React, { useState, useRef, useEffect } from 'react';
import { keyframes } from '@emotion/react';
import {
    Box,
    Button,
    Divider,
    TextField,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    Typography,
    Avatar,
    Fade,
    Collapse,
    Tooltip,
    IconButton as MuiIconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TuneIcon from '@mui/icons-material/Tune';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import SideMenu from '../components/navigation/SideMenu';
import AppNavbar from '../components/navigation/AppNavbar';
import axiosInstance from '../authentication/AuthContext';
import { useAuth } from '../authentication/AuthContext';
import Header from '../components/header/Header';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Mirrors the labels shown on the Preferences page so the badge here reads
// the same way no matter where a user sees their response mode.
const RESPONSE_MODE_LABELS = {
    generation: 'Generation',
    retrieval: 'Retrieval Only',
};

// Utility function to check if text is JSON and format it nicely
const isJsonString = (str) => {
    try {
        JSON.parse(str);
        return true;
    } catch (e) {
        return false;
    }
};

const formatJsonString = (str) => {
    try {
        const parsed = JSON.parse(str);
        return JSON.stringify(parsed, null, 2);
    } catch (e) {
        return str;
    }
};

// A retrieval-only chunk carries its own excerpt text and (optionally, per
// the show_citations preference) the page(s) it came from.
const normalizeChunk = (chunk) => ({
    content: chunk?.content ?? chunk?.text ?? '',
    pageNumbers: Array.isArray(
        chunk?.pages || chunk?.page_numbers || chunk?.pageNumbers
    )
        ? chunk.pages || chunk.page_numbers || chunk.pageNumbers
        : [],
});

const normalizeSources = (sources) =>
    Array.isArray(sources)
        ? sources
              .map((source, index) => ({
                  // Chunks from documents with no resolvable document_id
                  // (e.g. legacy embeddings) are grouped under a single
                  // trailing entry with documentId: null - still shown, just
                  // without a working "open original" link.
                  documentId: source?.document_id ?? source?.documentId ?? null,
                  title:
                      source?.title ||
                      source?.name ||
                      (source?.document_id != null
                          ? `Document ${source.document_id}`
                          : 'Other excerpts'),
                  downloadUrl:
                      source?.download_url || source?.downloadUrl || null,
                  pageNumbers: Array.isArray(
                      source?.pages ||
                          source?.page_numbers ||
                          source?.pageNumbers
                  )
                      ? source.pages || source.page_numbers || source.pageNumbers
                      : [],
                  chunks: Array.isArray(source?.chunks)
                      ? source.chunks.map(normalizeChunk)
                      : [],
                  // Inline citation markers like [1]/[2] in the answer text
                  // refer to a source's 1-based position in this array.
                  citationNumber: index + 1,
              }))
              .filter(
                  (source) => source.documentId != null || source.chunks.length > 0
              )
        : [];

const sortedUniquePages = (pageNumbers) =>
    Array.isArray(pageNumbers)
        ? [...new Set(pageNumbers)].sort((a, b) => a - b)
        : [];

// Formats page numbers for display, e.g. [3] -> "p. 3", [3, 4] -> "pp. 3, 4".
const formatPageNumbers = (pageNumbers) => {
    const sorted = sortedUniquePages(pageNumbers);
    if (sorted.length === 0) return null;
    return sorted.length === 1 ? `p. ${sorted[0]}` : `pp. ${sorted.join(', ')}`;
};

// Rewrites inline citation markers such as "[1]" or multi-cites like
// "[1][2]" into Markdown links pointing at "#cite-<n>", so react-markdown's
// existing link parsing does the heavy lifting - the `a` component override
// below then recognizes this href scheme and renders a citation badge
// instead of a normal link. The negative lookahead avoids mangling real
// Markdown links/reference definitions that happen to start with a number.
const linkifyCitations = (text) =>
    typeof text === 'string'
        ? text.replace(
              /\[(\d+)\](?!\(|:)/g,
              (match, num) => `[${num}](#cite-${num})`
          )
        : text;

const normalizeMessage = (message) => ({
    text: message?.text || message?.content || '',
    sender:
        message?.sender ||
        (message?.role === 'assistant' ? 'assistant' : 'user'),
    timestamp: message?.timestamp ? new Date(message.timestamp) : new Date(),
    sources: normalizeSources(message?.sources),
});

// react-markdown always passes a `node` prop (the underlying AST node) to
// custom renderers. It isn't a valid DOM/MUI attribute, so it must be
// stripped out before spreading the remaining props onto our elements.
const omitNode = ({ node, ...rest }) => rest; // eslint-disable-line unused-imports/no-unused-vars

// Maps Markdown elements to MUI components so assistant responses match the
// app's design system instead of relying on browser default styles.
// `sources` + `onCitationClick` let the `a` override turn "#cite-<n>" links
// (produced by linkifyCitations) into clickable badges tied to a source.
const createMarkdownComponents = (isUser, sources = [], onCitationClick) => ({
    h1: (props) => (
        <Typography
            variant="h6"
            sx={{
                mt: 1.5,
                mb: 0.5,
                fontWeight: 'bold',
                color: 'inherit',
                '&:first-of-type': { mt: 0 },
            }}
            {...omitNode(props)}
        />
    ),
    h2: (props) => (
        <Typography
            variant="subtitle1"
            sx={{
                mt: 1.5,
                mb: 0.5,
                fontWeight: 'bold',
                color: 'inherit',
                '&:first-of-type': { mt: 0 },
            }}
            {...omitNode(props)}
        />
    ),
    h3: (props) => (
        <Typography
            variant="subtitle2"
            sx={{
                mt: 1.5,
                mb: 0.5,
                fontWeight: 'bold',
                color: 'inherit',
                '&:first-of-type': { mt: 0 },
            }}
            {...omitNode(props)}
        />
    ),
    h4: (props) => (
        <Typography
            variant="subtitle2"
            sx={{
                mt: 1,
                mb: 0.5,
                fontWeight: 'bold',
                color: 'inherit',
                '&:first-of-type': { mt: 0 },
            }}
            {...omitNode(props)}
        />
    ),
    p: (props) => (
        <Typography
            sx={{
                lineHeight: 1.5,
                mb: 1,
                color: 'inherit',
                '&:last-child': { mb: 0 },
            }}
            {...omitNode(props)}
        />
    ),
    ul: (props) => (
        <Box
            component="ul"
            sx={{ pl: 3, mb: 1, '&:last-child': { mb: 0 } }}
            {...omitNode(props)}
        />
    ),
    ol: (props) => (
        <Box
            component="ol"
            sx={{ pl: 3, mb: 1, '&:last-child': { mb: 0 } }}
            {...omitNode(props)}
        />
    ),
    li: (props) => (
        <Typography
            component="li"
            sx={{ lineHeight: 1.5, mb: 0.5, color: 'inherit' }}
            {...omitNode(props)}
        />
    ),
    strong: (props) => (
        <Box component="strong" sx={{ fontWeight: 700 }} {...omitNode(props)} />
    ),
    em: (props) => (
        <Box component="em" sx={{ fontStyle: 'italic' }} {...omitNode(props)} />
    ),
    blockquote: (props) => (
        <Box
            component="blockquote"
            sx={{
                m: 0,
                mb: 1,
                pl: 1.5,
                borderLeft: 3,
                borderColor: isUser ? 'rgba(255,255,255,0.5)' : 'divider',
                color: isUser ? 'inherit' : 'text.primary',
            }}
            {...omitNode(props)}
        />
    ),
    a: (props) => {
        const { href, children, ...rest } = omitNode(props);
        if (typeof href === 'string' && href.startsWith('#cite-')) {
            const citationNumber = parseInt(href.slice('#cite-'.length), 10);
            const source = sources[citationNumber - 1];
            const isClickable = Boolean(source?.downloadUrl);
            return (
                <Tooltip
                    title={
                        source
                            ? source.title
                            : `Source ${citationNumber} unavailable`
                    }
                >
                    <Box
                        component="sup"
                        onClick={
                            isClickable
                                ? () => onCitationClick?.(source)
                                : undefined
                        }
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 15,
                            height: 15,
                            px: 0.4,
                            mx: 0.2,
                            fontSize: 10,
                            fontWeight: 700,
                            lineHeight: 1,
                            borderRadius: '999px',
                            verticalAlign: 'super',
                            textDecoration: 'none',
                            border: '1px solid',
                            borderColor: isUser
                                ? 'rgba(255,255,255,0.5)'
                                : 'primary.light',
                            bgcolor: isUser
                                ? 'rgba(255,255,255,0.2)'
                                : 'primary.mainLight',
                            color: isUser ? 'inherit' : 'primary.dark',
                            cursor: isClickable ? 'pointer' : 'default',
                            '&:hover': isClickable
                                ? {
                                      bgcolor: 'primary.main',
                                      color: 'white',
                                      borderColor: 'primary.main',
                                  }
                                : undefined,
                        }}
                    >
                        {citationNumber}
                    </Box>
                </Tooltip>
            );
        }
        return (
            <Box
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: isUser ? 'inherit' : 'primary.main' }}
                {...rest}
            >
                {children}
            </Box>
        );
    },
    hr: () => (
        <Divider
            sx={{
                my: 1,
                borderColor: isUser ? 'rgba(255,255,255,0.3)' : 'divider',
            }}
        />
    ),
    table: (props) => (
        <Box sx={{ overflowX: 'auto', mb: 1, '&:last-child': { mb: 0 } }}>
            <Box
                component="table"
                sx={{
                    borderCollapse: 'collapse',
                    width: '100%',
                    '& th, & td': {
                        border: 1,
                        borderColor: isUser
                            ? 'rgba(255,255,255,0.3)'
                            : 'divider',
                        px: 1,
                        py: 0.5,
                        textAlign: 'left',
                    },
                }}
                {...omitNode(props)}
            />
        </Box>
    ),
    pre: (props) => (
        <Box
            component="pre"
            sx={{
                bgcolor: isUser ? 'rgba(255,255,255,0.15)' : 'grey.100',
                color: isUser ? 'inherit' : 'text.primary',
                p: 1.5,
                borderRadius: 1,
                overflowX: 'auto',
                mb: 1,
                '&:last-child': { mb: 0 },
            }}
            {...omitNode(props)}
        />
    ),
    // react-markdown (v9+) no longer passes an `inline` boolean to `code`;
    // fenced blocks with a language tag get a `language-*` className (handled
    // by the `pre` override above), everything else is treated as inline code.
    code: ({ className, ...props }) => {
        const isFenced = /language-/.test(className || '');
        return (
            <Box
                component="code"
                className={className}
                sx={
                    isFenced
                        ? { fontFamily: 'monospace', fontSize: '0.85em' }
                        : {
                              bgcolor: isUser
                                  ? 'rgba(255,255,255,0.15)'
                                  : 'grey.100',
                              px: 0.6,
                              py: 0.1,
                              borderRadius: 0.5,
                              fontFamily: 'monospace',
                              fontSize: '0.85em',
                          }
                }
                {...omitNode(props)}
            />
        );
    },
});

const getConversationTitle = (conversation, index) =>
    conversation?.title ||
    conversation?.name ||
    conversation?.subject ||
    `Conversation ${index + 1}`;

const formatConversationDate = (value) => {
    if (!value) return '';
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return '';
    return parsedDate.toLocaleDateString();
};

const typingBounce = keyframes`
    0%, 80%, 100% {
        transform: scale(0.6);
        opacity: 0.4;
    }
    40% {
        transform: scale(1);
        opacity: 1;
    }
`;

// Classic "chat bubble typing" animation - three dots bouncing in sequence -
// so it's visually obvious something is actively happening while waiting on
// the RAG pipeline, rather than a static label that could look stalled.
const TypingIndicator = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontStyle: 'italic' }}
        >
            STACY is thinking
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {[0, 1, 2].map((i) => (
                <Box
                    key={i}
                    sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        animation: `${typingBounce} 1.2s ease-in-out infinite`,
                        animationDelay: `${i * 0.15}s`,
                    }}
                />
            ))}
        </Box>
    </Box>
);

export default function Chat() {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isCheckingChatReady, setIsCheckingChatReady] = useState(true);
    const [isChatReady, setIsChatReady] = useState(true);
    const [chatReadinessMessage, setChatReadinessMessage] = useState('');
    const [missingLlmApiKey, setMissingLlmApiKey] = useState(false);
    const [missingRerankerApiKey, setMissingRerankerApiKey] = useState(false);
    const [missingEmbeddingApiKey, setMissingEmbeddingApiKey] = useState(false);
    const { accessToken } = useAuth();
    const messagesEndRef = useRef(null);
    const [conversationId, setConversationId] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [downloadingSourceId, setDownloadingSourceId] = useState(null);
    const [conversationToDelete, setConversationToDelete] = useState(null);
    const [deletingConversation, setDeletingConversation] = useState(false);
    const [deleteConversationError, setDeleteConversationError] =
        useState(null);
    const [responseMode, setResponseMode] = useState(null);
    const navigate = useNavigate();

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        const checkChatReadiness = async () => {
            setIsCheckingChatReady(true);
            try {
                const response = await axiosInstance.get(
                    '/chat/generate-response/',
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );

                const ready = Boolean(response.data?.is_chat_ready);
                setIsChatReady(ready);
                setMissingLlmApiKey(
                    Boolean(response.data?.missing_llm_api_key)
                );
                setMissingRerankerApiKey(
                    Boolean(response.data?.missing_reranker_api_key)
                );
                setMissingEmbeddingApiKey(
                    Boolean(response.data?.missing_embedding_api_key)
                );
                setChatReadinessMessage(response.data?.message || '');
            } catch (error) {
                console.error('Error checking chat readiness:', error);
                setIsChatReady(false);
                setChatReadinessMessage(
                    'Unable to verify chat configuration. Please try again later.'
                );
            } finally {
                setIsCheckingChatReady(false);
            }
        };

        checkChatReadiness();
    }, [accessToken]);

    // Surface the user's configured retrieval/response mode at the top of
    // the chat so it's clear whether answers come from the LLM
    // ("Generation") or are just ranked chunks ("Retrieval Only").
    useEffect(() => {
        const fetchResponseMode = async () => {
            try {
                const response = await axiosInstance.get('/preferences/', {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                const data =
                    response?.data?.preferences ?? response?.data ?? {};
                setResponseMode(data.response_mode || null);
            } catch (error) {
                console.error('Error loading response mode:', error);
            }
        };

        if (accessToken) {
            fetchResponseMode();
        }
    }, [accessToken]);

    // Send message to the API
    const sendMessage = async () => {
        if (input.trim() === '' || isLoading || !isChatReady) return;

        const userMessage = {
            text: input,
            sender: 'user',
            timestamp: new Date(),
            sources: [],
        };
        setMessages((prev) => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        try {
            const response = await axiosInstance.post(
                '/chat/generate-response/',
                {
                    message: userMessage.text,
                    conversation_id: conversationId,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            // store conversation_id from first response
            if (!conversationId) {
                setConversationId(response.data.conversation_id);
            }
            if (response.data.conversation_id) {
                setConversationId(response.data.conversation_id);
            }

            const rawAnswer = response.data.answer;

            if (rawAnswer === undefined) {
                throw new Error('Answer was not defined.');
            }

            const sources = normalizeSources(response.data.sources);
            // Retrieval-only mode returns answer: null - there's no
            // generated text, just ranked source chunks (rendered below via
            // the sources' `chunks`), so avoid dumping the raw JSON payload.
            const isRetrievalOnly = rawAnswer === null;

            let responseText;
            if (isRetrievalOnly) {
                responseText =
                    sources.length > 0
                        ? ''
                        : 'No relevant documents were found for your question.';
            } else if (
                rawAnswer &&
                typeof rawAnswer === 'object' &&
                rawAnswer.answer
            ) {
                responseText = rawAnswer.answer;
            } else {
                responseText =
                    response.data.message ||
                    response.data.response ||
                    rawAnswer ||
                    response.data.content ||
                    JSON.stringify(response.data);
            }

            // add assistant response to UI
            const assistantMessage = {
                text: responseText,
                sender: 'assistant',
                timestamp: new Date(),
                sources,
            };
            setMessages((prev) => [...prev, assistantMessage]);
            fetchConversations();
        } catch (error) {
            console.error('Error sending message:', error);

            // More specific error handling
            let errorText = 'Sorry, I encountered an error. Please try again.';
            if (error.response?.status === 401) {
                errorText = 'Please log in again to continue the conversation.';
            } else if (error.response?.status === 429) {
                errorText =
                    'Too many requests. Please wait a moment before trying again.';
            } else if (error.response?.data?.message) {
                errorText = error.response.data.message;
            }

            const errorMessage = {
                text: errorText,
                sender: 'assistant',
                timestamp: new Date(),
                sources: [],
            };
            setMessages((prev) => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const loadConversation = async (id) => {
        try {
            const response = await axiosInstance.get(
                `/chat/conversations/${id}/`
            );
            setConversationId(id);
            setMessages((response.data.messages || []).map(normalizeMessage));
        } catch (error) {
            console.error('Error loading conversation:', error);
        }
    };

    const startNewConversation = () => {
        setConversationId(null);
        setMessages([]);
    };

    const handleDeleteConversation = async () => {
        if (!conversationToDelete) return;

        const idToDelete = conversationToDelete.id;
        try {
            setDeletingConversation(true);
            setDeleteConversationError(null);
            await axiosInstance.delete(
                `/chat/conversations/${idToDelete}/delete/`
            );

            setConversations((prev) =>
                prev.filter((conversation) => conversation.id !== idToDelete)
            );

            // If the deleted conversation was open, drop back to a blank
            // conversation instead of leaving stale messages on screen.
            if (conversationId === idToDelete) {
                startNewConversation();
            }
            setConversationToDelete(null);
        } catch (error) {
            console.error('Error deleting conversation:', error);
            setDeleteConversationError(
                'Failed to delete conversation. Please try again.'
            );
        } finally {
            setDeletingConversation(false);
        }
    };

    const getApiOrigin = () => {
        const baseURL = axiosInstance.defaults.baseURL || '';
        try {
            return new URL(baseURL, window.location.origin).origin;
        } catch {
            return '';
        }
    };

    // Fetches a source document's file as a blob, preserving the server's
    // Content-Type (defaulting to PDF, the common case for sources) so the
    // browser knows how to render it instead of showing raw/illegible bytes.
    const fetchSourceBlob = async (source) => {
        const response = await axiosInstance.get(source.downloadUrl, {
            baseURL: getApiOrigin(),
            responseType: 'blob',
        });
        const contentType =
            response.headers?.['content-type'] || 'application/pdf';
        const blobUrl = window.URL.createObjectURL(
            new Blob([response.data], { type: contentType })
        );
        return { blobUrl, contentType };
    };

    // Opens a source document in a new browser tab so the user can view it
    // (most browsers render PDFs/images inline instead of downloading them).
    // When the source has page references, jumps straight to the first
    // cited page via the PDF viewer's "#page=" URL fragment.
    const handleViewSource = async (source) => {
        if (!source?.downloadUrl || downloadingSourceId) return;

        // Open the tab synchronously (before the async fetch) so browsers
        // don't treat it as an unsolicited popup and block it.
        const viewerWindow = window.open('', '_blank');

        setDownloadingSourceId(source.documentId);
        try {
            const { blobUrl, contentType } = await fetchSourceBlob(source);
            const [firstPage] = sortedUniquePages(source.pageNumbers);
            const viewUrl =
                contentType.includes('pdf') && firstPage
                    ? `${blobUrl}#page=${firstPage}`
                    : blobUrl;

            if (viewerWindow) {
                viewerWindow.location.href = viewUrl;
            } else {
                // Popup was blocked - fall back to a direct download instead.
                const link = window.document.createElement('a');
                link.href = blobUrl;
                link.download = source.title || `document-${source.documentId}`;
                window.document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error('Failed to open source document:', error);
            viewerWindow?.close();
        } finally {
            setDownloadingSourceId(null);
        }
    };

    // Downloads a source document referenced by the assistant's answer,
    // saving it directly to disk instead of opening it in a new tab.
    const handleDownloadSource = async (source) => {
        if (!source?.downloadUrl || downloadingSourceId) return;

        setDownloadingSourceId(source.documentId);
        try {
            const { blobUrl } = await fetchSourceBlob(source);
            const link = window.document.createElement('a');
            link.href = blobUrl;
            link.download = source.title || `document-${source.documentId}`;
            window.document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Failed to download source document:', error);
        } finally {
            setDownloadingSourceId(null);
        }
    };

    const fetchConversations = async () => {
        try {
            const response = await axiosInstance.get('/chat/conversations/');
            setConversations(response.data);
        } catch (error) {
            console.error('Error fetching conversations:', error);
        }
    };

    // fetch on mount
    useEffect(() => {
        fetchConversations();
    }, []);

    // Renders a single source's citation badge, title (clickable to view
    // the document), page label, and download button. Shared by the flat
    // "Sources" list (generation mode) and the grouped excerpt view
    // (retrieval-only mode).
    const renderSourceHeader = (source) => {
        const isLink = Boolean(source.downloadUrl);
        const pageLabel = formatPageNumbers(source.pageNumbers);
        return (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 15,
                        height: 15,
                        px: 0.4,
                        fontSize: 10,
                        fontWeight: 700,
                        lineHeight: 1,
                        borderRadius: '999px',
                        flexShrink: 0,
                        border: '1px solid',
                        borderColor: 'primary.light',
                        bgcolor: 'primary.mainLight',
                        color: 'primary.dark',
                    }}
                >
                    {source.citationNumber}
                </Typography>
                <Tooltip
                    title={
                        isLink
                            ? 'Open document in a new tab'
                            : 'Document unavailable'
                    }
                >
                    <Typography
                        component="span"
                        variant="caption"
                        onClick={
                            isLink ? () => handleViewSource(source) : undefined
                        }
                        sx={{
                            fontSize: '0.7rem',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.3,
                            color: isLink ? 'primary.main' : 'text.secondary',
                            textDecoration: isLink ? 'underline' : 'none',
                            wordBreak: 'break-word',
                            cursor: isLink ? 'pointer' : 'default',
                            '&:hover': isLink
                                ? { color: 'primary.dark' }
                                : undefined,
                        }}
                    >
                        {source.title}
                        {isLink && (
                            <OpenInNewIcon
                                sx={{ fontSize: 11, flexShrink: 0 }}
                            />
                        )}
                    </Typography>
                </Tooltip>
                {pageLabel && (
                    <Typography
                        component="span"
                        variant="caption"
                        sx={{
                            fontSize: '0.7rem',
                            color: 'text.secondary',
                            flexShrink: 0,
                        }}
                    >
                        ({pageLabel})
                    </Typography>
                )}
                {isLink && (
                    <Tooltip title="Download">
                        <span>
                            <MuiIconButton
                                size="small"
                                onClick={() => handleDownloadSource(source)}
                                disabled={
                                    downloadingSourceId === source.documentId
                                }
                                sx={{ p: 0.25 }}
                            >
                                <DownloadIcon sx={{ fontSize: 12 }} />
                            </MuiIconButton>
                        </span>
                    </Tooltip>
                )}
            </Box>
        );
    };

    // Message component
    const MessageBubble = ({ message }) => {
        const isUser = message.sender === 'user';
        const [showJson, setShowJson] = useState(false);
        const isJson = isJsonString(message.text);
        const formattedJson = isJson
            ? formatJsonString(message.text)
            : message.text;
        // Retrieval-only responses have no generated text - instead each
        // source carries the ranked chunks that back it, rendered as
        // excerpts grouped under that source below.
        const hasChunkExcerpts =
            !isUser &&
            message.sources?.some((source) => source.chunks?.length > 0);

        return (
            <Fade in={true} timeout={300}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: isUser ? 'flex-end' : 'flex-start',
                        mb: 2,
                        px: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            maxWidth: '70%',
                            flexDirection: isUser ? 'row-reverse' : 'row',
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 32,
                                height: 32,
                                bgcolor: isUser
                                    ? 'primary.dark'
                                    : 'transparent',
                                color: 'white',
                            }}
                        >
                            {isUser ? (
                                <PersonIcon />
                            ) : (
                                <img
                                    src="/stacy_secure_ai_logo_circle.png"
                                    alt="STACY"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                    }}
                                />
                            )}
                        </Avatar>

                        <Paper
                            elevation={1}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: isUser
                                    ? 'primary.dark'
                                    : 'primary.mainLight',
                                color: isUser ? 'white' : 'text.primary',
                                position: 'relative',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 8,
                                    [isUser ? 'right' : 'left']: -6,
                                    width: 0,
                                    height: 0,
                                    borderTop: '6px solid transparent',
                                    borderBottom: '6px solid transparent',
                                    [isUser ? 'borderLeft' : 'borderRight']:
                                        `6px solid ${
                                            isUser
                                                ? 'primary.main'
                                                : 'theme.palette.divider'
                                        }`,
                                },
                            }}
                        >
                            {(message.text || isUser) &&
                                (isJson ? (
                                <Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="body3"
                                            sx={{
                                                fontWeight: 'bold',
                                                color: 'primary.main',
                                            }}
                                        >
                                            JSON Response
                                        </Typography>
                                        <MuiIconButton
                                            size="small"
                                            onClick={() =>
                                                setShowJson(!showJson)
                                            }
                                            sx={{ p: 0.5 }}
                                        >
                                            {showJson ? (
                                                <ExpandLessIcon />
                                            ) : (
                                                <ExpandMoreIcon />
                                            )}
                                        </MuiIconButton>
                                    </Box>
                                    <Collapse in={showJson}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 1.5,
                                                backgroundColor: 'grey.50',
                                                border: 1,
                                                borderColor: 'grey.200',
                                                borderRadius: 1,
                                                mb: 1,
                                            }}
                                        >
                                            <Typography
                                                component="pre"
                                                variant="caption"
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.75rem',
                                                    lineHeight: 1.4,
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    color: 'text.primary',
                                                }}
                                            >
                                                {formattedJson}
                                            </Typography>
                                        </Paper>
                                    </Collapse>
                                    {!showJson && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'text.secondary',
                                                fontStyle: 'italic',
                                            }}
                                        >
                                            Click to expand JSON
                                        </Typography>
                                    )}
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        color: isUser
                                            ? 'text.contrastText'
                                            : 'text.primary',
                                    }}
                                >
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={createMarkdownComponents(
                                            isUser,
                                            message.sources,
                                            handleViewSource
                                        )}
                                    >
                                        {isUser
                                            ? message.text
                                            : linkifyCitations(message.text)}
                                    </ReactMarkdown>
                                </Box>
                            ))}
                            {!isUser &&
                                !hasChunkExcerpts &&
                                message.sources?.length > 0 && (
                                <Box
                                    sx={{
                                        mt: 1.5,
                                        pt: 1,
                                        borderTop: 1,
                                        borderColor: 'divider',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.4,
                                    }}
                                >
                                    {message.sources.map((source) => (
                                        <React.Fragment
                                            key={source.citationNumber}
                                        >
                                            {renderSourceHeader(source)}
                                        </React.Fragment>
                                    ))}
                                </Box>
                            )}
                            {!isUser && hasChunkExcerpts && (
                                <Box
                                    sx={{
                                        mt: 1.5,
                                        pt: 1,
                                        borderTop: 1,
                                        borderColor: 'divider',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1.25,
                                    }}
                                >
                                    {message.sources.map((source) => (
                                        <Box key={source.citationNumber}>
                                            {renderSourceHeader(source)}
                                            {source.chunks.length > 0 && (
                                                <Box
                                                    sx={{
                                                        mt: 0.5,
                                                        pl: 2.5,
                                                        display: 'flex',
                                                        flexDirection:
                                                            'column',
                                                        gap: 0.75,
                                                    }}
                                                >
                                                    {source.chunks.map(
                                                        (chunk, chunkIndex) => {
                                                            const chunkPageLabel =
                                                                formatPageNumbers(
                                                                    chunk.pageNumbers
                                                                );
                                                            return (
                                                                <Paper
                                                                    key={
                                                                        chunkIndex
                                                                    }
                                                                    variant="outlined"
                                                                    sx={{
                                                                        p: 1,
                                                                        borderRadius: 1,
                                                                        backgroundColor:
                                                                            'background.paper',
                                                                        borderColor:
                                                                            'divider',
                                                                    }}
                                                                >
                                                                    {chunkPageLabel && (
                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={{
                                                                                display:
                                                                                    'block',
                                                                                fontWeight: 600,
                                                                                color: 'text.secondary',
                                                                                mb: 0.3,
                                                                            }}
                                                                        >
                                                                            {
                                                                                chunkPageLabel
                                                                            }
                                                                        </Typography>
                                                                    )}
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            whiteSpace:
                                                                                'pre-wrap',
                                                                            fontSize:
                                                                                '0.8rem',
                                                                        }}
                                                                    >
                                                                        {
                                                                            chunk.content
                                                                        }
                                                                    </Typography>
                                                                </Paper>
                                                            );
                                                        }
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    mt: 1,
                                    opacity: 0.7,
                                    fontSize: '0.7rem',
                                }}
                            >
                                {message.timestamp.toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                })}
                            </Typography>
                        </Paper>
                    </Box>
                </Box>
            </Fade>
        );
    };

    MessageBubble.propTypes = {
        message: PropTypes.shape({
            text: PropTypes.string.isRequired,
            sender: PropTypes.oneOf(['user', 'assistant']).isRequired,
            timestamp: PropTypes.instanceOf(Date).isRequired,
            sources: PropTypes.arrayOf(
                PropTypes.shape({
                    documentId: PropTypes.oneOfType([
                        PropTypes.string,
                        PropTypes.number,
                    ]),
                    title: PropTypes.string,
                    downloadUrl: PropTypes.string,
                    pageNumbers: PropTypes.arrayOf(PropTypes.number),
                    citationNumber: PropTypes.number,
                    chunks: PropTypes.arrayOf(
                        PropTypes.shape({
                            content: PropTypes.string,
                            pageNumbers: PropTypes.arrayOf(PropTypes.number),
                        })
                    ),
                })
            ),
        }).isRequired,
    };

    return (
        <Box sx={{ display: 'flex', width: '100%', overflow: 'hidden' }}>
            <SideMenu />
            <AppNavbar />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: '100%',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                }}
            >
                <Header />
                <Box
                    sx={{
                        bgcolor: 'background.default',
                        flex: 1,
                        width: '100%',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        pb: 5,
                        mt: { xs: 8, md: 0 },
                        px: { xs: 2, sm: 3 },
                        py: { xs: 2, sm: 3 },
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            height: 'calc(100vh - 120px)',
                        }}
                    >
                        <Paper
                            sx={{
                                width: { xs: '100%', md: 320 },
                                minWidth: { md: 320 },
                                height: '100%',
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: 'background.paper',
                            }}
                        >
                            <Box
                                sx={{
                                    p: 2,
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography variant="h6" sx={{ mb: 1.5 }}>
                                    Conversations
                                </Typography>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={startNewConversation}
                                >
                                    New Chat
                                </Button>
                            </Box>
                            <Box sx={{ flex: 1, overflow: 'auto' }}>
                                {conversations.length === 0 ? (
                                    <Box sx={{ p: 3, textAlign: 'center' }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            No conversations yet
                                        </Typography>
                                    </Box>
                                ) : (
                                    <List sx={{ p: 0 }}>
                                        {conversations.map(
                                            (conversation, index) => (
                                                <React.Fragment
                                                    key={
                                                        conversation.id || index
                                                    }
                                                >
                                                    <ListItem
                                                        disablePadding
                                                        secondaryAction={
                                                            <Tooltip title="Delete conversation">
                                                                <span>
                                                                    <MuiIconButton
                                                                        edge="end"
                                                                        size="small"
                                                                        onClick={(
                                                                            e
                                                                        ) => {
                                                                            e.stopPropagation();
                                                                            setDeleteConversationError(
                                                                                null
                                                                            );
                                                                            setConversationToDelete(
                                                                                conversation
                                                                            );
                                                                        }}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </MuiIconButton>
                                                                </span>
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <ListItemButton
                                                            selected={
                                                                conversationId ===
                                                                conversation.id
                                                            }
                                                            onClick={() =>
                                                                loadConversation(
                                                                    conversation.id
                                                                )
                                                            }
                                                            disabled={isLoading}
                                                            sx={{
                                                                pr: 6,
                                                                '&.Mui-selected':
                                                                    {
                                                                        backgroundColor:
                                                                            'primary.light',
                                                                        '&:hover':
                                                                            {
                                                                                backgroundColor:
                                                                                    'primary.light',
                                                                            },
                                                                    },
                                                            }}
                                                        >
                                                            <ListItemText
                                                                primary={
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        noWrap
                                                                    >
                                                                        {getConversationTitle(
                                                                            conversation,
                                                                            index
                                                                        )}
                                                                    </Typography>
                                                                }
                                                                secondary={
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                    >
                                                                        {formatConversationDate(
                                                                            conversation.updated_at ||
                                                                                conversation.created_at
                                                                        ) ||
                                                                            `ID: ${
                                                                                conversation.id
                                                                            }`}
                                                                    </Typography>
                                                                }
                                                            />
                                                        </ListItemButton>
                                                    </ListItem>
                                                    {index <
                                                        conversations.length -
                                                            1 && <Divider />}
                                                </React.Fragment>
                                            )
                                        )}
                                    </List>
                                )}
                            </Box>
                        </Paper>

                        <Paper
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                height: '100%',
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: 'background.paper',
                            }}
                        >
                            {/* Begin of Chat Area */}
                            <Box
                                sx={{
                                    flex: 1,
                                    overflow: 'auto',
                                    py: 2,
                                    px: 1,
                                    width: '100%',
                                    maxWidth: '100%',
                                }}
                            >
                                {messages.length === 0 ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                            color: 'text.secondary',
                                        }}
                                    >
                                        <Box sx={{ mb: 2, opacity: 0.3 }}>
                                            <img
                                                src="/stacy_secure_ai_logo_circle.png"
                                                alt="STACY"
                                                style={{
                                                    width: '64px',
                                                    height: '64px',
                                                    objectFit: 'contain',
                                                }}
                                            />
                                        </Box>
                                        <Typography variant="h6" gutterBottom>
                                            STACY is ready to help you.
                                        </Typography>
                                        <Typography variant="body2">
                                            Start a conversation.
                                        </Typography>
                                        {isCheckingChatReady && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    mt: 1,
                                                    color: 'text.secondary',
                                                }}
                                            >
                                                Checking chat readiness...
                                            </Typography>
                                        )}
                                        {!isCheckingChatReady &&
                                            !isChatReady && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        mt: 1,
                                                        color: 'error.main',
                                                    }}
                                                >
                                                    {chatReadinessMessage ||
                                                        'Chat is not ready. Missing required API keys.'}
                                                    {(missingLlmApiKey ||
                                                        missingRerankerApiKey) &&
                                                        ' Missing:'}
                                                    {missingLlmApiKey &&
                                                        ' model API key'}
                                                    {missingLlmApiKey &&
                                                        missingRerankerApiKey &&
                                                        ','}
                                                    {missingRerankerApiKey &&
                                                        ' reranker API key'}
                                                    {missingEmbeddingApiKey &&
                                                        ' embedding API key'}
                                                    .
                                                </Typography>
                                            )}
                                    </Box>
                                ) : (
                                    messages.map((message, index) => (
                                        <MessageBubble
                                            key={index}
                                            message={message}
                                        />
                                    ))
                                )}

                                {isLoading && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-start',
                                            mb: 2,
                                            px: 2,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 1,
                                            }}
                                        >
                                            <Avatar
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    bgcolor: 'transparent',
                                                    color: 'white',
                                                }}
                                            >
                                                <img
                                                    src="/stacy_secure_ai_logo_circle.png"
                                                    alt="STACY"
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'contain',
                                                    }}
                                                />
                                            </Avatar>
                                            <Paper
                                                elevation={1}
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    backgroundColor:
                                                        'background.paper',
                                                }}
                                            >
                                                <TypingIndicator />
                                            </Paper>
                                        </Box>
                                    </Box>
                                )}
                                <div ref={messagesEndRef} />
                            </Box>

                            {/* Input Area */}
                            {!isCheckingChatReady && !isChatReady ? (
                                <Box
                                    sx={{
                                        p: 2,
                                        borderTop: 1,
                                        borderColor: 'divider',
                                        width: '100%',
                                        maxWidth: '100%',
                                    }}
                                ></Box>
                            ) : (
                                <Box
                                    sx={{
                                        p: 2,
                                        borderTop: 1,
                                        borderColor: 'divider',
                                        backgroundColor: 'background.default',
                                        width: '100%',
                                        maxWidth: '100%',
                                        flex: 1,
                                        maxHeight: '300px',
                                        overflow: 'scroll',
                                    }}
                                >
                                    {responseMode && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                px: 1,
                                            }}
                                        >
                                            <Tooltip
                                                title={
                                                    responseMode ===
                                                    'retrieval'
                                                        ? 'Retrieval Only: answers show ranked source chunks without calling the LLM. Change this in Preferences.'
                                                        : 'Generation: answers are generated by the LLM using retrieved sources. Change this in Preferences.'
                                                }
                                            >
                                                <Chip
                                                    icon={<TuneIcon />}
                                                    label={
                                                        RESPONSE_MODE_LABELS[
                                                            responseMode
                                                        ] || responseMode
                                                    }
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() =>
                                                        navigate(
                                                            '/preferences'
                                                        )
                                                    }
                                                    sx={{
                                                        backgroundColor:
                                                            responseMode ===
                                                            'retrieval'
                                                                ? 'primary.light'
                                                                : 'primary.mainLight',
                                                        cursor: 'pointer',
                                                    }}
                                                />
                                            </Tooltip>
                                        </Box>
                                    )}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                            alignItems: 'flex-end',
                                            width: '100%',
                                            maxWidth: '100%',
                                            mx: 'auto',
                                            py: 2,
                                            px: 1,
                                        }}
                                    >
                                        <TextField
                                            fullWidth
                                            multiline
                                            maxRows={8}
                                            variant="outlined"
                                            placeholder="Type your message..."
                                            value={input}
                                            onChange={(e) =>
                                                setInput(e.target.value)
                                            }
                                            onKeyDown={(e) => {
                                                if (
                                                    e.key === 'Enter' &&
                                                    !e.shiftKey
                                                ) {
                                                    e.preventDefault();
                                                    sendMessage();
                                                }
                                            }}
                                            disabled={
                                                isLoading || isCheckingChatReady
                                            }
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 3,
                                                    backgroundColor:
                                                        'background.paper',
                                                },
                                            }}
                                        />
                                        <IconButton
                                            color="primary"
                                            onClick={sendMessage}
                                            disabled={
                                                !input.trim() ||
                                                isLoading ||
                                                isCheckingChatReady ||
                                                !isChatReady
                                            }
                                            sx={{
                                                bgcolor: 'primary.main',
                                                color: 'text.contrastText',
                                                '&:hover': {
                                                    bgcolor: 'primary.dark',
                                                },
                                                '&:disabled': {
                                                    bgcolor: 'grey.300',
                                                    color: 'grey.500',
                                                },
                                            }}
                                        >
                                            <SendIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            )}
                            {/** End of Chat Area */}
                        </Paper>
                    </Box>
                </Box>
            </Box>
            <Dialog
                open={Boolean(conversationToDelete)}
                onClose={() =>
                    !deletingConversation && setConversationToDelete(null)
                }
            >
                <DialogTitle>Delete Conversation</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete{' '}
                        <strong>
                            {conversationToDelete
                                ? getConversationTitle(
                                      conversationToDelete,
                                      conversations.indexOf(
                                          conversationToDelete
                                      )
                                  )
                                : 'this conversation'}
                        </strong>
                        ? This action cannot be undone.
                    </DialogContentText>
                    {deleteConversationError && (
                        <Typography
                            variant="body2"
                            color="error"
                            sx={{ mt: 2 }}
                        >
                            {deleteConversationError}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConversationToDelete(null)}
                        disabled={deletingConversation}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConversation}
                        color="error"
                        variant="contained"
                        disabled={deletingConversation}
                    >
                        {deletingConversation ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
